import * as React from "react";
import { useMatch } from "react-router";

import { useNavigation } from "@/providers";
import { ThreadTimestampFormatProvider } from "@/shared/components/RobotoMarkdown/timeFormat";
import { Goto } from "@/shared/navigation";
import { type CollaborationBarLocation } from "@/shared/state/collaborationBar";

import styles from "./AgentThreadSidebar.module.css";
import { AgentThreadSidebarControls } from "./AgentThreadSidebarControls";
import { AgentThreadSidebarMainContent } from "./AgentThreadSidebarMainContent";
import { AIAgentThreadProvider } from "./AIAgentThreadContext";

interface AgentThreadSidebarProps {
  /** Where the collaboration bar is pointed. The sidebar is a pure function of
   *  it: ``threadId`` is the conversation on screen, ``view`` picks the pane. */
  location: CollaborationBarLocation;

  /** Point the bar somewhere else. The sidebar never mutates its own address —
   *  it requests the move, the way the thread detail page navigates its URL,
   *  and the new address flows back down. */
  onNavigate: (location: CollaborationBarLocation) => void;

  onClose: () => void;
}

export const AgentThreadSidebar = ({
  location,
  onNavigate,
  onClose,
}: AgentThreadSidebarProps) => {
  const showHistory = location.view === "history";
  const { goto } = useNavigation();

  // Matching the detail route exactly also excludes
  // `/agents/threads/:threadId/fork`, which carries a thread id but mounts no
  // conversation to collide with.
  //
  // The null check is redundant today, but keeps "no thread" from ever being
  // an address that can match: off-route, `params.threadId` is `undefined`, so
  // an address that grew an `undefined` member would collide app-wide.
  const pageThreadMatch = useMatch(`${Goto.route.AgentThreads}/:threadId`);
  const threadShownOnPage =
    location.threadId !== null &&
    location.threadId === pageThreadMatch?.params.threadId
      ? location.threadId
      : null;

  const showThread = React.useCallback(
    (threadId: string | null) => {
      onNavigate({ mode: "ai-agent-chat", threadId, view: "thread" });
    },
    [onNavigate],
  );

  // Closing the sidebar behind the navigation: two open panes on one thread
  // would both accept messages.
  const handleOpenThreadInFullView = (threadId: string) => {
    goto.agentThread({ threadId });
    onClose();
  };

  // The address uses `null` for a new thread because it has to survive
  // sessionStorage, where `undefined` does not round-trip. Both providers below
  // read the `undefined` convention their existing consumers already use, so
  // the conversion happens once, here.
  const threadId = location.threadId ?? undefined;

  return (
    <AIAgentThreadProvider
      threadId={threadId}
      onThreadIdChanged={(nextThreadId) => showThread(nextThreadId ?? null)}
    >
      <ThreadTimestampFormatProvider threadId={threadId}>
        <div className={styles.container}>
          <AgentThreadSidebarControls
            onClose={onClose}
            showHistory={showHistory}
            onShowHistory={(nextShowHistory) => {
              onNavigate({
                ...location,
                view: nextShowHistory ? "history" : "thread",
              });
            }}
          />
          <AgentThreadSidebarMainContent
            showHistory={showHistory}
            threadShownOnPage={threadShownOnPage}
            onThreadClicked={showThread}
            onOpenThreadInFullView={handleOpenThreadInFullView}
          />
        </div>
      </ThreadTimestampFormatProvider>
    </AIAgentThreadProvider>
  );
};
